import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Table } from "@pentabd/ui";
import { CommitteeTableColumn } from "./CommitteeTableColumn";
import { useNavigate } from "react-router";
import { useCommitteeList } from "@hooks/procurements/committee/useCommitteeList";
import { CommitteeResponseType } from "@type/procurements/committee";
import { Pagination } from "@type/procurements/pagination";
import { useLanguage } from "@hooks/useLanguage";
import { DeleteConfirmationModal, ErrorModal, SuccessModal } from "@components";
import useActionCreator from "@redux/hooks/useActionCreator";
import SearchController from "@components/SearchController";
import { TablePagination } from "@helpers/tablePagination";

export default function CommitteeListTable() {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();
  const [itemName, setItemName] = useState<string>("");

  const { language } = useLanguage();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { committeeDeleteActionHandler } = useActionCreator();

  const { t } = useTranslation();
  const { control } = useForm();

  const navigate = useNavigate();

  const { handleQuery, committeeState } = useCommitteeList();

  const { getData, deleteSuccess, deleteError } = committeeState;

  const { data, pagination } = getData || ({} as CommitteeResponseType);
  const { page, total } = pagination || ({} as Pagination);

  const deleteSuccessAction = () => {
    setIsSuccessModalOpen(false);
    handleQuery("page", page);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const closeConfirmationModel = () => setIsConfirmationModalOpen(false);

  const openConfirmationModel = (itemName: string, id: string | number) => {
    setItemId(id as number);
    setItemName(itemName);
    setIsConfirmationModalOpen(true);
  };

  const deleteAction = () => {
    closeConfirmationModel();
    committeeDeleteActionHandler(itemId as number);
  };

  useEffect(() => {
    if (deleteSuccess) {
      setIsSuccessModalOpen(true);
    }

    if (!!deleteError) {
      setIsErrorModalOpen(true);
    }
  }, [deleteSuccess, deleteError]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsConfirmationModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <div className="pt-10">
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={deleteSuccessAction}
          subtitle={`You have deleted a procurement committee`}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={
            !!deleteError
              ? deleteError?.response?.data?.message
              : "Something went wrong"
          }
        />
      )}
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          subtitle={itemName}
          isModalOpen={isConfirmationModalOpen}
          onClose={closeConfirmationModel}
          action={deleteAction}
        />
      )}

      {/* modals end */}
      <Table
        header={{
          title: t("PROCUREMENT_COMMITTEE_TABLE.TABLE.LABEL"),
          badge: {
            label: `${total || 0}`,
            size: "sm",
            type: "primary",
          },

          buttons: [
            <SearchController
              control={control}
              name="search"
              onChange={(value) => {
                handleQuery("search", value);
              }}
            />,
          ],
        }}
        columns={CommitteeTableColumn(
          t,
          language,
          navigate,
          openConfirmationModel,
        )}
        // edit={{
        //   customIcon: <IconEdit01 size={24} fill="subtitle1" />,
        //   onClick: (id) => {
        //     navigate(`${id}`);
        //   },
        // }}
        // remove={{
        //   customIcon: <IconTrash01 size={24} fill="danger" />,
        //   onClick: (id) => {
        //     committeeDeleteActionHandler(id);
        //   },
        // }}
        rows={data || []}
        pagination={TablePagination(pagination, handleQuery)}
      />
    </div>
  );
}
